/**
 * Contentlaag. Elk bezoekersleesbaar veld is `Tekst = { nl, en }` — geen tweede
 * bestand voor Engels, want twee parallelle bestanden lopen onzichtbaar uit de
 * pas (PLAN.md §4.7). `en: null` betekent: nog niet vertaald. De TaalScope
 * beoordeelt volledigheid per scherm en valt op Nederlands terug.
 */
export type Taal = 'nl' | 'en'
export type Tekst = { nl: string; en: string | null }

export const t = (tekst: Tekst, taal: Taal): string => tekst[taal] ?? tekst.nl
export const isVertaald = (tekst: Tekst): boolean => tekst.en !== null

export type ModuleId = 'automatisering' | 'veiligheid' | 'energie' | 'connectiviteit' | InfomoduleId

/** De onderdelen zonder eigen flow: een infoscherm voor het gesprek met sales (21 oktober). */
export type InfomoduleId = 'stellingen' | 'tennant'

/**
 * De video die bij een keuze hoort. Sinds de feedback van 5 oktober heeft elke
 * keuze in Automatisering en Veiligheid haar eigen film (PLAN.md §0.3, §0.8);
 * sinds 21 oktober start die niet meer vanzelf maar met de filmknop.
 *
 * `src` wijst naar een bestand onder `public/video/`, nooit naar YouTube of
 * Vimeo: de kiosk moet de film zonder netwerk kunnen afspelen. Zolang
 * Marketing de standaardvideo's niet heeft geleverd staat hier per onderdeel
 * een plaatsvervanger van tien seconden uit `scripts/maak-plaatsvervangers.sh`.
 */
export interface Video {
  src: string
  /** Kop in het videovenster. */
  titel: Tekst
  /** Ondertitelbestanden, per taal; `null` zolang ze er niet zijn (§4.8.7). */
  ondertitels: { nl: string | null; en: string | null }
}

export interface Module {
  id: ModuleId
  nr: string
  naam: Tekst
  sub: Tekst
  /** Wat het magazijn doet als deze laag actief is — de kern van §1. */
  laag: Tekst
}

const plaatsvervanger = (bestand: string, titel: Tekst): Video => ({
  src: `/video/${bestand}.mp4`,
  titel,
  ondertitels: { nl: null, en: null },
})

export const MODULES: Module[] = [
  {
    id: 'automatisering',
    nr: '01',
    naam: { nl: 'Automatisering', en: 'Automation' },
    sub: { nl: 'Interactief magazijn', en: 'Interactive warehouse' },
    laag: { nl: 'De trucks komen tot leven', en: 'The trucks come to life' },
  },
  {
    id: 'veiligheid',
    nr: '02',
    naam: { nl: 'Veiligheid', en: 'Safety' },
    sub: { nl: 'Ontdek veiligheidsoplossingen', en: 'Discover safety solutions' },
    laag: { nl: 'Looproutes en blinde hoeken lichten op', en: 'Walkways and blind corners light up' },
  },
  {
    id: 'energie',
    nr: '03',
    naam: { nl: 'Energie', en: 'Energy' },
    sub: { nl: 'Van netcongestie naar slim laden', en: 'From grid congestion to smart charging' },
    laag: { nl: 'De laadstraat licht op', en: 'The charging lane lights up' },
  },
  {
    id: 'connectiviteit',
    nr: '04',
    naam: { nl: 'Connectiviteit', en: 'Connectivity' },
    sub: { nl: 'Ontdek Warehouse Cockpit en myLinde', en: 'Discover Warehouse Cockpit and myLinde' },
    laag: { nl: 'De digital twin legt zich erover heen', en: 'The digital twin overlays the space' },
  },
  // Stellingen en Tennant (feedback 21 oktober): naast de route van de
  // flowchart, als aanleiding voor het gesprek met sales. De hotspot-id
  // `stellingen` in Automatisering is een andere naamruimte.
  {
    id: 'stellingen',
    nr: '05',
    naam: { nl: 'Stellingen', en: 'Racking' },
    sub: { nl: 'Opslag die bij je magazijn past', en: 'Storage that fits your warehouse' },
    laag: { nl: 'De stellingen zelf in beeld', en: 'The racking itself in view' },
  },
  {
    id: 'tennant',
    nr: '06',
    naam: { nl: 'Tennant', en: 'Tennant' },
    sub: { nl: 'Professionele vloerreiniging', en: 'Professional floor cleaning' },
    laag: { nl: 'Een schone vloer in beeld', en: 'A clean floor in view' },
  },
]

/* ---------- Stellingen en Tennant ---------- */

export interface Infomodule {
  /** Kop op het podium. */
  kop: Tekst
  /** Eén of twee zinnen; de briefing vraagt weinig tekst. */
  intro: Tekst
  punten: Tekst[]
  /**
   * De film, als die er is. Zonder film geen filmknop: een plaatsvervanger
   * hoort niet op de beurs, dus komt er vóór de freeze geen echte film, dan
   * gaat dit veld eruit.
   */
  video?: Video
  /**
   * QR naar meer informatie: de korte route `/s/<sleutel>`, die de Worker
   * doorleidt (`worker/index.ts`, `EXTERN`). Zonder doel geen QR.
   */
  qr?: { sleutel: string; kop: Tekst }
}

/**
 * CONCEPTTEKST — door Marketing na te kijken of te vervangen. Sober en
 * feitelijk gehouden: Stellingen volgt de stellingenbrochure van Motrac
 * (palletstellingen, grootvakstellingen, inrijstellingen; ontwerp en
 * engineering), Tennant blijft bij wat Tennant maakt.
 */
export const INFOMODULES: Record<InfomoduleId, Infomodule> = {
  stellingen: {
    kop: { nl: 'Stellingen die bij je magazijn passen', en: 'Racking that fits your warehouse' },
    intro: {
      nl: 'Van palletstelling tot inrijstelling: Motrac ontwerpt en levert de opslag die past bij je goederen en je trucks.',
      en: 'From pallet racking to drive-in racking: Motrac designs and supplies the storage that fits your goods and your trucks.',
    },
    punten: [
      { nl: 'Palletstellingen', en: 'Pallet racking' },
      { nl: 'Grootvakstellingen', en: 'Long-span shelving' },
      { nl: 'Inrijstellingen', en: 'Drive-in racking' },
      { nl: 'Ontwerp en engineering', en: 'Design and engineering' },
    ],
    video: plaatsvervanger('stellingen', { nl: 'Stellingen van Motrac', en: 'Racking by Motrac' }),
    qr: { sleutel: 'stellingen', kop: { nl: 'Brochure', en: 'Brochure' } },
  },
  tennant: {
    kop: { nl: 'Een schone vloer, elke dienst', en: 'A clean floor, every shift' },
    intro: {
      nl: 'Schrob- en veegmachines van Tennant houden de magazijnvloer schoon — van compacte loopmachines tot opzitmachines voor grote hallen.',
      en: 'Tennant scrubbers and sweepers keep the warehouse floor clean — from compact walk-behind machines to ride-on machines for large halls.',
    },
    punten: [
      { nl: 'Schrobzuigmachines', en: 'Scrubber-dryers' },
      { nl: 'Veegmachines', en: 'Sweepers' },
      { nl: 'Loop- en opzitmodellen', en: 'Walk-behind and ride-on' },
    ],
    video: plaatsvervanger('tennant', { nl: 'Tennant vloerreiniging', en: 'Tennant floor cleaning' }),
  },
}

export interface Hotspot {
  id: string
  truck: 'K-Matic' | 'T-Matic' | 'R-Matic' | 'L-Matic'
  /** Korte kop op het scherm. De briefing eist weinig tekst. */
  kop: Tekst
  /** Positie in het magazijn — verwijst naar een gangpad uit de indeling. */
  gangpad: number
  x: number
  uitdaging: Tekst
  oplossing: Tekst
  voordelen: Tekst[]
  /** De film van deze machine — achter de filmknop, na de vlucht naar de hotspot (flowchart). */
  video: Video
}

/**
 * De vier hotspots, letterlijk volgens de flowchart van 5 oktober
 * (docs/referenties/Flowchart_applicatie.pdf, PLAN.md §0.8): koppen, volgorde
 * en machine per hotspot zijn de tekening. Hotspot 1 (pallets uit stellingen
 * halen) is daar de K-MATIC, hotspot 3 (werken in smalle gangpaden) de
 * R-MATIC. Oplossing en voordelen horen bij de machine en zijn feiten van
 * Linde Material Handling (linde-mh.com, productpagina's en VDI-typebladen) —
 * zie `docs/machineteksten.md` voor de bron per zin.
 *
 * Eerder stonden ze andersom, met de redenering dat de R-MATIC als reachtruck
 * bij stellingen hoort en de K-MATIC als smallegangentruck bij smalle gangen.
 * Op 6 oktober is besloten dat de flowchart leidend is.
 */
export const HOTSPOTS: Hotspot[] = [
  {
    id: 'stellingen',
    kop: { nl: 'Pallets uit stellingen halen', en: 'Retrieving pallets from racking' },
    truck: 'K-Matic',
    gangpad: 0,
    x: 2,
    uitdaging: {
      nl: 'Het opslaan en uitslaan van pallets kost veel tijd. Het werk is repetitief en chauffeurs zijn lastig te vinden.',
      en: 'Storing and retrieving pallets takes a lot of time. The work is repetitive and drivers are hard to find.',
    },
    oplossing: {
      nl: 'Autonome smallegangentruck met draaikop die pallets tot 1.450 kg zelf opslaat en uitslaat, tot 14 meter hoog.',
      en: 'Autonomous very narrow aisle truck with a turret head that stores and retrieves pallets of up to 1,450 kg on its own, up to 14 metres high.',
    },
    voordelen: [
      { nl: 'Hefhoogte tot 14 m', en: 'Lift height up to 14 m' },
      { nl: '3D-palletherkenning', en: '3D pallet recognition' },
      { nl: 'Geen extra infrastructuur', en: 'No extra infrastructure' },
      { nl: 'Overdag handmatig, ’s avonds automatisch', en: 'Manual by day, automated in the evening' },
    ],
    video: plaatsvervanger('k-matic', { nl: 'Linde K-MATIC in bedrijf', en: 'Linde K-MATIC at work' }),
  },
  {
    id: 'transport',
    kop: { nl: 'Transport tussen productie en magazijn', en: 'Transport between production and warehouse' },
    truck: 'T-Matic',
    gangpad: 1,
    x: 4,
    uitdaging: {
      nl: 'Pallets moeten continu van A naar B. Dat vraagt veel handmatige ritten.',
      en: 'Pallets move from A to B all day. That takes a lot of manual trips.',
    },
    oplossing: {
      nl: 'Autonome pallettruck die lasten tot 3.000 kg over lange afstanden verplaatst.',
      en: 'Autonomous pallet truck that moves loads of up to 3,000 kg over long distances.',
    },
    voordelen: [
      { nl: 'Zonder rails of reflectoren', en: 'No rails or reflectors' },
      { nl: 'Past zijn snelheid aan', en: 'Adapts its speed' },
      { nl: 'Alleen of in een vloot', en: 'Solo or as a fleet' },
      { nl: 'Handmatig via de dissel', en: 'Manual via the tiller' },
    ],
    video: plaatsvervanger('t-matic', { nl: 'Linde T-MATIC in bedrijf', en: 'Linde T-MATIC at work' }),
  },
  {
    id: 'smalgangpad',
    kop: { nl: 'Werken in smalle gangpaden', en: 'Working in narrow aisles' },
    truck: 'R-Matic',
    gangpad: 2,
    x: 0,
    uitdaging: {
      nl: 'De opslagdichtheid moet omhoog, maar de ruimte is beperkt.',
      en: 'Storage density has to go up, but space is limited.',
    },
    oplossing: {
      nl: 'Autonome reachtruck voor pallets tot 1.600 kg, tot 11 meter hoog, in werkgangen vanaf 2,90 meter.',
      en: 'Autonomous reach truck for pallets up to 1,600 kg, up to 11 metres high, in aisles from 2.90 metres.',
    },
    voordelen: [
      { nl: 'Werkgang vanaf 2,90 m', en: 'Aisles from 2.90 m' },
      { nl: '3D-camera vindt vak en pallet', en: '3D camera finds slot and pallet' },
      { nl: 'Geen extra infrastructuur', en: 'No extra infrastructure' },
      { nl: 'Ook handmatig te rijden', en: 'Can also be driven manually' },
    ],
    video: plaatsvervanger('r-matic', { nl: 'Linde R-MATIC in bedrijf', en: 'Linde R-MATIC at work' }),
  },
  {
    id: 'productie',
    kop: { nl: 'Productiebevoorrading', en: 'Production supply' },
    truck: 'L-Matic',
    gangpad: 3,
    x: 6,
    uitdaging: {
      nl: 'Productielijnen moeten continu van grondstoffen worden voorzien.',
      en: 'Production lines need a continuous supply of materials.',
    },
    oplossing: {
      nl: 'Compacte autonome palletstapelaar die productielijnen en transportbanden bevoorraadt.',
      en: 'Compact autonomous pallet stacker that supplies production lines and conveyors.',
    },
    voordelen: [
      { nl: '1.200 kg, heft tot 1,84 m', en: '1,200 kg, lifts to 1.84 m' },
      { nl: '360°-veiligheidsscanner', en: '360° safety scanner' },
      { nl: 'Naast mensen en gewone trucks', en: 'Alongside people and manual trucks' },
      { nl: 'Gangpad onder 2,5 m', en: 'Aisles under 2.5 m' },
    ],
    video: plaatsvervanger('l-matic', { nl: 'Linde L-MATIC in bedrijf', en: 'Linde L-MATIC at work' }),
  },
]

/* ---------- Veiligheid ---------- */

export interface Veiligheidsuitdaging {
  id: string
  /** Het antwoord op "Welke veiligheidsuitdaging herken jij het meest?" */
  uitdaging: Tekst
  /** Naam van de oplossing, zoals op de knop. */
  oplossing: string
  omschrijving: Tekst
  voordelen: Tekst[]
  video: Video
}

export const VEILIGHEID = {
  vraag: {
    nl: 'Welke veiligheidsuitdaging herken jij het meest in jouw magazijn?',
    en: 'Which safety challenge do you recognise most in your warehouse?',
  } as Tekst,
}

/**
 * De vier uitdagingen en oplossingen uit de flowchart van 5 oktober. De
 * omschrijvingen zijn kort gehouden en op de productpagina's van Linde
 * Material Handling nagelopen; Marketing kijkt ze na vóór de freeze
 * (`docs/machineteksten.md`).
 */
export const VEILIGHEIDSUITDAGINGEN: Veiligheidsuitdaging[] = [
  {
    id: 'voetgangers',
    uitdaging: { nl: 'Voetgangers en trucks werken dicht op elkaar', en: 'Pedestrians and trucks work close together' },
    oplossing: 'Linde Safety Guard',
    omschrijving: {
      nl: 'Waarschuwt chauffeur én voetganger zodra ze elkaar te dicht naderen — ook door stellingen en om hoeken heen.',
      en: 'Warns driver and pedestrian as soon as they get too close — even through racking and around corners.',
    },
    voordelen: [
      { nl: 'Werkt ook zonder zicht', en: 'Works without line of sight' },
      { nl: 'Instelbare afstand per zone', en: 'Adjustable distance per zone' },
      { nl: 'Vertraagt de truck automatisch', en: 'Slows the truck automatically' },
    ],
    video: plaatsvervanger('safety-guard', { nl: 'Linde Safety Guard', en: 'Linde Safety Guard' }),
  },
  {
    id: 'kruisingen',
    uitdaging: { nl: 'Beperkt zicht op kruisingen en blinde hoeken', en: 'Limited view at crossings and blind corners' },
    // Reverse Assist in plaats van BlueSpot (Marketing, 6 oktober); de film is
    // de echte, aangeleverd op 6 oktober, en staat in de bundel.
    oplossing: 'Linde Reverse Assist',
    omschrijving: {
      nl: 'Zodra de truck achteruit rijdt ziet de chauffeur op zijn display wat er achter hem gebeurt — ook op kruisingen en in blinde hoeken.',
      en: 'As soon as the truck reverses, the driver sees on the display what is happening behind it — at crossings and in blind corners too.',
    },
    voordelen: [
      { nl: 'Schakelt vanzelf in bij achteruitrijden', en: 'Switches on automatically when reversing' },
      { nl: 'Zicht achter de truck zonder omdraaien', en: 'View behind the truck without turning round' },
      { nl: 'Minder aanrijdingen op kruisingen', en: 'Fewer collisions at crossings' },
    ],
    video: {
      src: '/video/reverse-assist.mp4',
      titel: { nl: 'Linde Reverse Assist', en: 'Linde Reverse Assist' },
      ondertitels: { nl: null, en: null },
    },
  },
  {
    id: 'rijgedrag',
    uitdaging: { nl: 'Controle houden op snelheid en rijgedrag', en: 'Keeping control of speed and driving behaviour' },
    oplossing: 'myLinde',
    omschrijving: {
      nl: 'Ziet per truck en per chauffeur hoe er gereden wordt: snelheid, schokken, aanrijdingen — en stelt snelheidszones in.',
      en: 'Shows how each truck and driver is driven: speed, shocks, collisions — and sets speed zones.',
    },
    voordelen: [
      { nl: 'Snelheidszones per gebied', en: 'Speed zones per area' },
      { nl: 'Schokmelding met chauffeur', en: 'Shock alerts with driver' },
      { nl: 'Toegang alleen met rijbevoegdheid', en: 'Access only with authorisation' },
    ],
    video: plaatsvervanger('mylinde', { nl: 'myLinde', en: 'myLinde' }),
  },
  {
    id: 'deuren',
    uitdaging: { nl: 'Aanrijdingen van deuren en doorgangen', en: 'Collisions with doors and passages' },
    oplossing: 'Linde DoorControl',
    omschrijving: {
      nl: 'Opent de deur alleen als de truck erdoor past — mast omlaag, snelheid omlaag — en houdt hem anders dicht.',
      en: 'Opens the door only when the truck fits through — mast down, speed down — and keeps it closed otherwise.',
    },
    voordelen: [
      { nl: 'Geen beschadigde deuren', en: 'No damaged doors' },
      { nl: 'Automatisch, zonder afstandsbediening', en: 'Automatic, no remote' },
      { nl: 'Koppelt met Safety Guard', en: 'Links with Safety Guard' },
    ],
    video: plaatsvervanger('doorcontrol', { nl: 'Linde DoorControl', en: 'Linde DoorControl' }),
  },
]

/* ---------- Energie ---------- */

export const ENERGIE = {
  kop: { nl: 'Energiecalculator', en: 'Energy calculator' } as Tekst,
  intro: {
    nl: 'Stel je vloot in en zie direct wat lithium-ion tegenover loodzuur betekent.',
    en: 'Set up your fleet and see straight away what lithium-ion means compared to lead-acid.',
  } as Tekst,
  instellingen: {
    trucks: { nl: 'Aantal trucks', en: 'Number of trucks' } as Tekst,
    shifts: { nl: 'Aantal shifts', en: 'Number of shifts' } as Tekst,
    uren: { nl: 'Draaiuren per truck', en: 'Operating hours per truck' } as Tekst,
    laadtijd: { nl: 'Beschikbare laadtijd', en: 'Available charging time' } as Tekst,
  },
  perDag: { nl: 'per dag', en: 'per day' } as Tekst,
  uurPerDag: { nl: 'uur/dag', en: 'h/day' } as Tekst,
  lithium: { nl: 'Lithium-ion', en: 'Lithium-ion' } as Tekst,
  loodzuur: { nl: 'Loodzuur', en: 'Lead-acid' } as Tekst,
  uitkomst: {
    batterijen: { nl: 'Batterijen nodig', en: 'Batteries needed' } as Tekst,
    wissels: { nl: 'Batterijwissels per dag', en: 'Battery changes per day' } as Tekst,
    energie: { nl: 'Stroomverbruik per dag', en: 'Energy use per day' } as Tekst,
    laadvermogen: { nl: 'Piekvermogen laden', en: 'Peak charging power' } as Tekst,
  },
  pastNiet: {
    nl: 'Past niet in de beschikbare laadtijd',
    en: 'Does not fit in the available charging time',
  } as Tekst,
  aanname: {
    nl: 'Indicatie op basis van gemiddelde waarden. De aannames staan in de rekenmotor en worden door Product ingevuld.',
    en: 'Indication based on average values. The assumptions live in the calculation engine and are filled in by Product.',
  } as Tekst,
}

/* ---------- Connectiviteit ---------- */

export const CONNECTIVITEIT = {
  kop: { nl: 'Warehouse Cockpit', en: 'Warehouse Cockpit' } as Tekst,
  intro: {
    nl: 'Ontdek hoe myLinde vlootbeheer, veiligheid en energie in één cockpit samenbrengt.',
    en: 'Discover how myLinde brings fleet management, safety and energy together in one cockpit.',
  } as Tekst,
  laden: { nl: 'Warehouse Cockpit laden…', en: 'Loading Warehouse Cockpit…' } as Tekst,
  offline: {
    nl: 'Warehouse Cockpit heeft een internetverbinding nodig en die is er nu niet. Vraag onze collega’s op de stand naar een demonstratie.',
    en: 'Warehouse Cockpit needs an internet connection and there is none right now. Ask our colleagues at the stand for a demonstration.',
  } as Tekst,
  url: 'https://www.warehousecockpit.com',
}

/**
 * De campagnezin is Engels en blijft dat in beide talen — het is een merknaam
 * van de campagne, geen zin om te vertalen (feedback 5 oktober). De tweede
 * regel krijgt de merkkleur.
 */
export const HOOFDSCHERM = {
  /** De rode regel boven de kop, zoals Linde die boven elke campagnekop zet. */
  kicker: { nl: 'Motrac × Linde · Logistica Next 2026', en: 'Motrac × Linde · Logistica Next 2026' } as Tekst,
  hero: {
    nl: 'From Warehouse\nto Powerhouse',
    en: 'From Warehouse\nto Powerhouse',
  } as Tekst,
  onderkop: {
    nl: 'Ontdek waar jouw magazijn slimmer kan.',
    en: 'Discover where your warehouse can be smarter.',
  } as Tekst,
}

/**
 * Teksten van het videovenster en het aftelscherm. Hier en niet in de
 * componenten, zodat ze meetellen in de EN-dekking.
 */
export const VENSTER = {
  sluiten: { nl: 'Sluiten', en: 'Close' } as Tekst,
  opnieuw: { nl: 'Opnieuw', en: 'Replay' } as Tekst,
  verder: { nl: 'Verder ontdekken', en: 'Keep exploring' } as Tekst,
  film: { nl: 'Bekijk de video', en: 'Watch the video' } as Tekst,
  /** Het afspeelteken in het venster, als de film stilstaat. */
  afspelen: { nl: 'Afspelen', en: 'Play' } as Tekst,
}

export const AFTELLEN = {
  kop: { nl: 'Ben je er nog?', en: 'Still there?' } as Tekst,
  uitleg: {
    nl: 'Raak het scherm aan om verder te gaan. Anders begint de volgende bezoeker met een schoon scherm.',
    en: 'Touch the screen to continue. Otherwise the next visitor starts with a clean screen.',
  } as Tekst,
  knop: { nl: 'Ik ben er nog', en: "I'm still here" } as Tekst,
}
